import React, { useCallback, useEffect, useRef, useState } from "react";
import { REELS } from "./instagramPosts";

function ReelTile({
  url,
  videoSrc,
  poster,
  index,
  focusIndex,
  onDesktopPointerEnter,
  mobileActive = false,
  mobileNear = true,
  playHoverDriven = true,
}) {
  const [hovered, setHovered] = useState(false);
  const videoRef = useRef(null);
  const tileRef = useRef(null);
  // The active reel only plays (and therefore downloads) once the strip is near the viewport;
  // on a phone the reels sit several screens below the fold.
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = tileRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      rootMargin: "200px 0px",
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  // Phones: only fetch metadata for the active reel and its neighbours (8 reels ≈ 33 MB otherwise).
  // Once a tile has been near the viewport we keep its data rather than throwing it away.
  const [warmed, setWarmed] = useState(mobileNear);
  useEffect(() => {
    if (mobileNear) setWarmed(true);
  }, [mobileNear]);
  const focused = focusIndex !== null && focusIndex === index;
  const leftOfFocus = focusIndex !== null && index < focusIndex;
  const rightOfFocus = focusIndex !== null && index > focusIndex;
  const shouldPlay = (mobileActive && inView) || (playHoverDriven && hovered);

  const pauseAndReset = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    try {
      v.currentTime = 0;
    } catch {
      /* ignore */
    }
  }, []);

  // iOS refuses play() outside a gesture when Low Power Mode is on or "Auto-Play Video
  // Previews" is off; the poster stays visible and we try once more on the next touch.
  useEffect(() => {
    if (!shouldPlay) {
      pauseAndReset();
      return undefined;
    }
    const v = videoRef.current;
    if (!v) return undefined;
    let cancelled = false;
    const GESTURES = ["touchend", "pointerup", "keydown"];
    const retry = () => {
      GESTURES.forEach((ev) => document.removeEventListener(ev, retry));
      if (!cancelled) v.play().catch(() => {});
    };
    v.play().catch(() => {
      if (cancelled) return;
      GESTURES.forEach((ev) => document.addEventListener(ev, retry, { passive: true }));
    });
    return () => {
      cancelled = true;
      GESTURES.forEach((ev) => document.removeEventListener(ev, retry));
    };
  }, [shouldPlay, pauseAndReset]);

  const openReel = useCallback(() => {
    window.open(url, "_blank", "noopener,noreferrer");
  }, [url]);

  const tileClass = [
    "reel-grid__tile",
    focused ? "is-focused" : "",
    leftOfFocus ? "is-left-of-focus" : "",
    rightOfFocus ? "is-right-of-focus" : "",
    mobileActive ? "is-mobile-active" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={tileRef}
      className={tileClass}
      tabIndex={0}
      role="group"
      aria-label={
        mobileActive
          ? "Studio reel — playing, tap to open on Instagram"
          : "Studio reel — hover to play on desktop, tap to open on Instagram"
      }
      onPointerEnter={() => {
        setHovered(true);
        onDesktopPointerEnter(index);
      }}
      onPointerLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onClick={openReel}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openReel();
        }
      }}
    >
      <div className="reel-grid__media">
        <video
          ref={videoRef}
          className="reel-grid__video"
          src={videoSrc}
          poster={poster}
          muted
          playsInline
          loop
          preload={warmed ? "metadata" : "none"}
        />
      </div>
    </div>
  );
}

const MOBILE_CAROUSEL_MQ = "(max-width: 900px)";

export default function InstagramFeed() {
  const [focusIndex, setFocusIndex] = useState(null);
  const [mobileSnapIndex, setMobileSnapIndex] = useState(0);
  // Read synchronously so the first render already knows which reels to preload.
  const [isMobileCarousel, setIsMobileCarousel] = useState(
    () => typeof window !== "undefined" && window.matchMedia(MOBILE_CAROUSEL_MQ).matches,
  );
  const stripRef = useRef(null);
  const scrollIdleRef = useRef(null);

  const onDesktopPointerEnter = useCallback((i) => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(hover: hover) and (min-width: 901px)").matches) {
      setFocusIndex(i);
    }
  }, []);

  const clearDesktopFocus = useCallback(() => {
    setFocusIndex(null);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia(MOBILE_CAROUSEL_MQ);
    const sync = () => setIsMobileCarousel(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const updateSnapFromScroll = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const tiles = strip.querySelectorAll(".reel-grid__tile");
    if (!tiles.length) return;
    const mid = strip.getBoundingClientRect().left + strip.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    tiles.forEach((el, i) => {
      const r = el.getBoundingClientRect();
      const c = r.left + r.width / 2;
      const dist = Math.abs(c - mid);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    setMobileSnapIndex((prev) => (prev === best ? prev : best));
  }, []);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip || !isMobileCarousel) return undefined;

    const onScroll = () => {
      updateSnapFromScroll();
      if (scrollIdleRef.current) clearTimeout(scrollIdleRef.current);
      scrollIdleRef.current = setTimeout(updateSnapFromScroll, 120);
    };

    const onScrollEnd = () => updateSnapFromScroll();

    strip.addEventListener("scroll", onScroll, { passive: true });
    strip.addEventListener("scrollend", onScrollEnd);
    const ro = new ResizeObserver(() => updateSnapFromScroll());
    ro.observe(strip);
    const t = requestAnimationFrame(() => updateSnapFromScroll());

    return () => {
      cancelAnimationFrame(t);
      strip.removeEventListener("scroll", onScroll);
      strip.removeEventListener("scrollend", onScrollEnd);
      ro.disconnect();
      if (scrollIdleRef.current) clearTimeout(scrollIdleRef.current);
    };
  }, [isMobileCarousel, updateSnapFromScroll]);

  const scrollToMobileIndex = useCallback((idx) => {
    const strip = stripRef.current;
    if (!strip) return;
    const tiles = strip.querySelectorAll(".reel-grid__tile");
    const el = tiles[idx];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development" || process.env.REACT_APP_DEBUG_REELS !== "1") return undefined;
    REELS.forEach((r, i) => {
      const el = document.createElement("video");
      el.preload = "metadata";
      el.src = r.videoSrc;
      el.addEventListener(
        "loadeddata",
        () => {
          // eslint-disable-next-line no-console
          console.info(`[reels] #${i + 1} OK`, r.videoSrc);
        },
        { once: true }
      );
      el.addEventListener(
        "error",
        () => {
          // eslint-disable-next-line no-console
          console.warn(`[reels] #${i + 1} failed to load`, r.videoSrc);
        },
        { once: true }
      );
    });
  }, []);

  const gridClass =
    focusIndex !== null ? "reel-grid reel-grid--has-focus" : "reel-grid";

  const atStart = mobileSnapIndex <= 0;
  const atEnd = mobileSnapIndex >= REELS.length - 1;

  return (
    <div className={`reel-carousel${isMobileCarousel ? " reel-carousel--mobile" : ""}`}>
      {isMobileCarousel ? (
        <>
          <button
            type="button"
            className="reel-carousel__arrow reel-carousel__arrow--prev"
            aria-label="Previous reel"
            disabled={atStart}
            onClick={() => scrollToMobileIndex(Math.max(0, mobileSnapIndex - 1))}
          >
            <span aria-hidden="true">‹</span>
          </button>
          <button
            type="button"
            className="reel-carousel__arrow reel-carousel__arrow--next"
            aria-label="Next reel"
            disabled={atEnd}
            onClick={() =>
              scrollToMobileIndex(Math.min(REELS.length - 1, mobileSnapIndex + 1))
            }
          >
            <span aria-hidden="true">›</span>
          </button>
        </>
      ) : null}
      <div
        ref={stripRef}
        className={gridClass}
        data-focused-index={focusIndex ?? ""}
        onPointerLeave={clearDesktopFocus}
      >
        {REELS.map((reel, i) => (
          <ReelTile
            key={reel.url}
            url={reel.url}
            videoSrc={reel.videoSrc}
            poster={reel.poster}
            index={i}
            focusIndex={focusIndex}
            onDesktopPointerEnter={onDesktopPointerEnter}
            mobileActive={isMobileCarousel && i === mobileSnapIndex}
            mobileNear={!isMobileCarousel || Math.abs(i - mobileSnapIndex) <= 1}
            playHoverDriven={!isMobileCarousel}
          />
        ))}
      </div>
    </div>
  );
}
